import React, { useEffect, useRef } from "react";
import { Button, ColorInput, Container, DEFAULT_THEME, Divider, Group, MantineStyleProps, Paper, ScrollArea, Select, SelectProps, Space, Stack, Text, TextInput, Textarea, ThemeIcon, Title, useMantineTheme } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertTriangle, IconBolt, IconCheck, IconChevronLeft, IconClipboard, IconFileCheck, IconFilePencil, IconRepeat, IconShield, IconSnowflake, IconTool, IconTag, IconLock, IconTrash } from "@tabler/icons-react";

import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../RoleTemplate";
import { useAccess } from "../../Auth/auth-functions";
import { CategoryDto, MutableCategoryDto } from "../../models/categories.models";
import classes from "./CategoryDetailView.module.css";

interface CategoryEditViewProps {
	category?: CategoryDto | null;
	onSave: (category: MutableCategoryDto) => void;
	onClose: () => void;
	isSaving?: boolean;
	onChanged?: (changed: boolean) => void;
}

export const CategoryEditView: React.FC<CategoryEditViewProps> = ({
	category,
	onSave,
	onClose,
	isSaving = false,
	onChanged,
}) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const isNew = !category || category.CategoryId === "new";
	const canEditCategory = useAccess(AzureADRoles.Admin);

	const form = useForm<MutableCategoryDto>({
		initialValues: {
			CategoryId: category?.CategoryId || "",
			Title: category?.Title || "",
			Description: category?.Description || "",
			Color: category?.Color || "#5c7cfa",
			Icon: category?.Icon || "icon-tag",
		},
		validate: {
			Title: (value) => (value.length < 3 ? "Title must be at least 3 characters long" : null),
			Color: (value) => (value ? null : "Color is required"),
			Icon: (value) => (value == null || value === "" ? "Icon is required" : null),
		},
	});

	// Track dirty state and call onChanged
	const prevDirty = useRef(form.isDirty());
	useEffect(() => {
		if (onChanged && prevDirty.current !== form.isDirty() && canEditCategory) {
			onChanged(form.isDirty());
			prevDirty.current = form.isDirty();
		}
	}, [form.isDirty(), onChanged]);

	useEffect(() => {
		form.setValues({
			Title: category?.Title || "",
			Description: category?.Description || "",
			Color: category?.Color || "#5c7cfa",
			Icon: category?.Icon || "icon-tag",
		});
		// eslint-disable-next-line
	}, [category]);

	const handleSave = (values: typeof form.values) => {
		if (!form.isValid()) {
			form.validate();
			return;
		}
		onSave(values);
	};

	if (category?.Deleted != null) {
		return (
			<Paper withBorder p="md" radius="md" style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 158px)' }}>
				<Stack gap="xs" mt="xl" justify="center" align="center">
					<ThemeIcon size={80} radius={80} color="red" variant="light">
						<IconTrash size={40} />
					</ThemeIcon>
					<Text fz="xl" fw="bold">
						Category Deleted
					</Text>
					<Text ta="center">This category has been deleted and cannot be edited.</Text>
				</Stack>
			</Paper>
		);
	}

	const noAccess = (
		<Paper withBorder p="md" radius="md" style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 158px)' }}>
			<Stack gap="xs" mt="xl" justify="center" align="center">
				<ThemeIcon size={80} radius={80} color="gray" variant="light">
					<IconLock size={40} />
				</ThemeIcon>
				<Text fz="xl" fw="bold">
					No Access
				</Text>
				<Text ta="center">You do not have permission to create or edit categories. Please contact your administrator.</Text>
			</Stack>
		</Paper>
	);

	return <RoleTemplate role={AzureADRoles.Admin} noAccess={noAccess}>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<Button variant="subtle" radius="xl" ml={-15} onClick={onClose} miw={100} leftSection={<IconChevronLeft size={20} />}>
								Back
							</Button>
							<Title order={isMobile ? 6 : 3}>{category ? "Edit Category" : "New Category"}</Title>
						</Group>
						<Button
							onClick={() => handleSave(form.values)}
							disabled={!form.isDirty()}
							loading={isSaving}
							variant="filled"
							radius="xl"
							miw={100}
							leftSection={<IconCheck />}
						>
							Save
						</Button>
					</Group>
				</Stack>
				<Container px="20" mb="lg">
					<form onSubmit={(e) => { e.preventDefault(); }}>
						<Button type="submit" style={{ display: 'none' }} />
						<TextInput
							label="Title"
							key={form.key("Title")}
							{...form.getInputProps("Title")}
							placeholder="Enter category title"
							maxLength={255}
							required
							mb="xl"
						/>
						<Textarea
							label="Description"
							key={form.key("Description")}
							{...form.getInputProps("Description")}
							placeholder="Enter category description"
							mb="xl"
							autosize
							minRows={3}
						/>

						<ColorInput
							key={form.key("Color")}
							{...form.getInputProps("Color")}
							mb="xl"
							placeholder="Pick color"
							label="Color"
							disallowInput
							required
							withPicker={false}
							swatchesPerRow={10}
							swatches={[
								...DEFAULT_THEME.colors.red,
								...DEFAULT_THEME.colors.pink,
								...DEFAULT_THEME.colors.grape,
								...DEFAULT_THEME.colors.violet,
								...DEFAULT_THEME.colors.indigo,
								...DEFAULT_THEME.colors.blue,
								...DEFAULT_THEME.colors.cyan,
								...DEFAULT_THEME.colors.green,
								...DEFAULT_THEME.colors.teal,
								...DEFAULT_THEME.colors.lime,
								...DEFAULT_THEME.colors.yellow,
								...DEFAULT_THEME.colors.orange
							]}
						/>

						{/* <InputWrapper label="Color">
						<Group mb="xl" gap="xs">

						<ColorSwatch
							component="button"
							color="green"
							onClick={() => form.setFieldValue("Color", "green")}
							style={{ color: '#fff', cursor: 'pointer' }}
						>
							{form.values.Color === "green" && <CheckIcon size={12} />}
						</ColorSwatch>
						
						<ColorSwatch
							component="button"
							color="Red"
							onClick={() => form.setFieldValue("Color", "Red")}
							style={{ color: '#fff', cursor: 'pointer' }}
						>
							{form.values.Color === "Red" && <CheckIcon size={12} />}
						</ColorSwatch>

						</Group>
						</InputWrapper> */}

						{/* <TextInput
							label="Color"
							key={form.key("Color")}
							{...form.getInputProps("Color")}
							placeholder="Enter category color (e.g. blue, red, green)"
							maxLength={50}
							required
							mb="xl"
						/> */}
						{/* <TextInput
							label="Icon"
							key={form.key("Icon")}
							{...form.getInputProps("Icon")}
							placeholder="Enter category icon (e.g. icon-tag, icon-exclamation, icon-shield)"
							maxLength={50}
							required
							mb="xl"
						/> */}

						<IconSelect
							label="Icon"
							key={form.key("Icon")}
							{...form.getInputProps("Icon")}
							required
						/>
					</form>
				</Container>
			</ScrollArea>
		</Paper>
	</RoleTemplate>
};

export default CategoryEditView;

const IconSelect = (props: SelectProps) => {
	const iconProps = {
		stroke: 1.5,
		color: 'currentColor',
		// opacity: 0.6,
		size: 22,
	};

	const icons: Record<string, React.ReactNode> = {
		"icon-tag": <IconTag {...iconProps} />,
		"icon-exclamation": <IconAlertTriangle {...iconProps} />,
		"icon-bolt": <IconBolt {...iconProps} />,
		"icon-clipboard": <IconClipboard {...iconProps} />,
		"icon-tool": <IconTool {...iconProps} />,
		"icon-repeat": <IconRepeat {...iconProps} />,
		"icon-file-pencil": <IconFilePencil {...iconProps} />,
		"icon-snowflake": <IconSnowflake {...iconProps} />,
		"icon-shield": <IconShield {...iconProps} />,
		"icon-file-check": <IconFileCheck {...iconProps} />
	};
	
	const renderSelectOption: SelectProps['renderOption'] = ({ option, checked }) => (
		<Group flex="1" gap="xs">
			{icons[option.value]}
			{option.label}
			{checked && <IconCheck style={{ marginInlineStart: 'auto' }} {...iconProps} />}
		</Group>
	);

	return <Select
		{...props}
		placeholder="Select an icon"
		leftSection={icons[props.value ?? "icon-tag"]}
		data={[
			{ value: 'icon-tag', label: 'Tag' },
			{ value: 'icon-exclamation', label: 'Exclamation' },
			{ value: 'icon-bolt', label: 'Bolt' },
			{ value: 'icon-clipboard', label: 'Clipboard' },
			{ value: 'icon-tool', label: 'Tool' },
			{ value: 'icon-repeat', label: 'Repeat' },
			{ value: 'icon-file-pencil', label: 'File Pencil' },
			{ value: 'icon-snowflake', label: 'Snowflake' },
			{ value: 'icon-shield', label: 'Shield' },
			{ value: 'icon-file-check', label: 'File Check' }
		]}
		renderOption={renderSelectOption}
	/>;
}